import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Box,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import deliveryAgentsApi from "../../api/delivery-agents";

interface CreateDeliveryAgentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface DeliveryAgentFormData {
  name: string;
  email: string;
  phoneNumber: string;
  isActive: boolean;
  aadharNumber: string;
  bikeNumber: string;
  agentType: string;
  instantStatus: string;
  jobStartTime: string;
}

const CreateDeliveryAgentDialog: React.FC<CreateDeliveryAgentDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<DeliveryAgentFormData>({
    name: "",
    email: "",
    phoneNumber: "",
    isActive: true,
    aadharNumber: "",
    bikeNumber: "",
    agentType: "subscription",
    instantStatus: "OFF_DUTY",
    jobStartTime: "05:00 AM",
  });

  const [errors, setErrors] = useState<Partial<DeliveryAgentFormData>>({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "isActive" ? checked : value,
    }));

    // Clear error when user types
    if (errors[name as keyof DeliveryAgentFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<DeliveryAgentFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid 10-digit phone number";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.aadharNumber && !/^\d{12}$/.test(formData.aadharNumber)) {
      newErrors.aadharNumber = "Aadhar number should be 12 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await deliveryAgentsApi.createDeliveryAgent(formData);
      onSuccess();
      handleReset();
      onClose();
    } catch (error) {
      console.error("Error creating delivery agent:", error);
      // Handle API errors here if needed
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      email: "",
      phoneNumber: "",
      isActive: true,
      aadharNumber: "",
      bikeNumber: "",
      agentType: "subscription",
      instantStatus: "OFF_DUTY",
      jobStartTime: "05:00 AM",
    });
    setErrors({});
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Delivery Agent</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 300px" }}>
              <TextField
                name="name"
                label="Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name}
              />
            </Box>
            <Box sx={{ flex: "1 1 300px" }}>
              <TextField
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 300px" }}>
              <TextField
                name="phoneNumber"
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
              />
            </Box>
            <Box sx={{ flex: "1 1 300px" }}>
              <TextField
                name="aadharNumber"
                label="Aadhar Number"
                value={formData.aadharNumber}
                onChange={handleInputChange}
                fullWidth
                error={!!errors.aadharNumber}
                helperText={errors.aadharNumber}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 300px" }}>
              <TextField
                name="bikeNumber"
                label="Bike Number"
                value={formData.bikeNumber}
                onChange={handleInputChange}
                fullWidth
              />
            </Box>
            <Box
              sx={{ flex: "1 1 300px", display: "flex", alignItems: "center" }}
            >
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 300px" }}>
              <TextField
                select
                name="agentType"
                label="Agent Type"
                value={formData.agentType}
                onChange={handleInputChange}
                fullWidth
              >
                <MenuItem value="subscription">Subscription</MenuItem>
                <MenuItem value="instant">Instant</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ flex: "1 1 300px" }}>
              <TextField
                select
                name="instantStatus"
                label="Instant Status"
                value={formData.instantStatus}
                onChange={handleInputChange}
                fullWidth
              >
                <MenuItem value="AVAILABLE">AVAILABLE</MenuItem>
                <MenuItem value="ON_DELIVERY">ON DELIVERY</MenuItem>
                <MenuItem value="OFF_DUTY">OFF DUTY</MenuItem>
              </TextField>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 300px" }}>
              <TextField
                name="jobStartTime"
                label="Default Start Time (e.g. 05:00 AM)"
                value={formData.jobStartTime}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Box>
            <Box sx={{ flex: "1 1 300px" }} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleReset} color="secondary">
          Reset
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDeliveryAgentDialog;
